import { useCallback, useSyncExternalStore } from 'react';
import {
  type AiRunAnswerEvent,
  type AiRunDeltaEvent,
  type AiRunRecord,
  type AiRunStep,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';
import { connectStream } from '../../lib/sse';
import { tokenStore } from '../../lib/token-store';
import { randomUuid } from '../../lib/uuid';
import { aiRunStore, type AiClarifyPrompt, type AiRunView } from './run-store';

/** Lượt chạy đang được theo dõi, hoặc `null`. Dùng ở bảng bên phải. */
export function useAiRun(): AiRunView | null {
  return useSyncExternalStore(aiRunStore.subscribe, aiRunStore.getSnapshot).run;
}

/**
 * Bảng bên phải có đang mở không — cho nút mở nhanh trên thanh điều hướng và
 * cho nút nổi "cập nhật nhanh" tự tránh chỗ (APM-UX-ai1).
 */
export function useAiPanelOpen(): boolean {
  return useSyncExternalStore(aiRunStore.subscribe, aiRunStore.getSnapshot).open;
}

export interface TrackOptions {
  /** Tên skill phía backend — chỉ để hiện, không dùng để định tuyến. */
  readonly skill: string;
  /**
   * Dùng lại một mã dấu vết đã có, thay vì sinh mã mới.
   *
   * Cần cho vòng hỏi lại (APM-806): lượt sinh nháp và lượt trả lời câu hỏi là
   * **hai** lời gọi HTTP nhưng là **một** cuộc đối thoại. Cùng mã thì bảng theo
   * dõi đọc liền mạch hỏi → trả lời → kết quả; hai mã thì phần đầu biến mất
   * đúng lúc người dùng vừa trả lời xong.
   */
  readonly runId?: string;
  /** Tiêu đề tiếng Việt trên bảng theo dõi. */
  readonly label: string;
  /**
   * Hành động "Áp dụng": đưa kết quả từ bảng theo dõi vào giao diện chính.
   *
   * Bỏ trống khi lượt chạy đã tự đưa kết quả vào chỗ của nó (ví dụ nháp kế
   * hoạch — người dùng đang đứng ngay ở wizard). Đừng đặt một hành động rỗng
   * cho đủ bộ: một nút "Áp dụng" không làm gì còn tệ hơn không có nút nào.
   */
  readonly apply?: (result: never) => { label: string; run: () => void | Promise<void> };
  /** Một dòng nói kết quả là gì, đọc từ dữ liệu vừa nhận. */
  readonly describe?: (result: never) => string;
  /**
   * Lượt chạy kết thúc bằng **câu hỏi** thì đây là chỗ trả lời — APM-UX-ai1.
   *
   * Trả `null` khi kết quả không phải câu hỏi. Bỏ trống hẳn khi skill không bật
   * `allowClarify`: model không có tool `ask_user` thì nhánh này không xảy ra.
   */
  readonly clarify?: (result: never, runId: string) => AiClarifyPrompt | null;
  /**
   * `false` = theo dõi lượt chạy nhưng **không tự mở** bảng bên phải (bảng đang mở thì vẫn
   * mở, đang đóng thì vẫn đóng; nút AI trên thanh điều hướng mở lại được). Cho lượt chạy nhỏ,
   * lặp lại, có kết quả hiện ngay tại chỗ — dịch một khối ở chế độ Song ngữ (APM-BIL-qa): bảng
   * 380px bật ra mỗi lần bấm Dịch làm hai cột co lại. Mặc định mở.
   */
  readonly openPanel?: boolean;
}

/**
 * Chạy một tính năng AI **và kể lại từng bước** — APM-1302.
 *
 * ## Vì sao client sinh `runId`, không phải server
 *
 * Vì client phải mở kết nối theo dõi **trước** khi gửi yêu cầu. Nếu server cấp
 * mã trong phản hồi thì mã ấy về tay client sau khi mọi thứ đã chạy xong —
 * đúng lúc không còn gì để xem. `AiRunTracker` phát bù bước đã trôi qua nên
 * thứ tự hai lời gọi không quan trọng, nhưng mã phải có trước cả hai.
 *
 * ## Vì sao vẫn dùng kết quả của lời gọi HTTP
 *
 * Bước cuối trên stream chỉ nói "xong, bao nhiêu tiền". Kết quả thật đi về theo
 * phản hồi HTTP như trước — đường đã qua guard và repository có scope. Đẩy cả
 * kết quả qua SSE là tạo con đường thứ hai đưa dữ liệu vào client
 * (docs/api-conventions.md §8), và đó là con đường không ai nhớ kiểm quyền.
 */
export function useTrackedAiRun(): <T>(
  options: TrackOptions,
  call: (runId: string) => Promise<T>,
) => Promise<T> {
  return useCallback(async function track<T>(
    options: TrackOptions,
    call: (runId: string) => Promise<T>,
  ): Promise<T> {
    const runId = options.runId ?? randomUuid();
    const controller = new AbortController();

    aiRunStore.start({
      runId,
      skill: options.skill,
      label: options.label,
      abort: () => controller.abort(),
      ...(options.openPanel === false ? { openPanel: false } : {}),
    });

    void connectStream({
      url: `/api/v1/ai/runs/${runId}/stream`,
      getToken: () => tokenStore.get(),
      signal: controller.signal,
      onEvent: (event) => {
        if (event.event === 'step') aiRunStore.step(event.data as AiRunStep);
        // Văn xuôi model đang gõ — bản chụp, thay nguyên khối (2026-09-29).
        if (event.event === 'delta') aiRunStore.delta((event.data as AiRunDeltaEvent).text);
        // Câu trả lời đang viết cho khung chat — APM-CHAT2-m.
        if (event.event === 'answer') aiRunStore.answer((event.data as AiRunAnswerEvent).text);
        // Server không còn dấu vết trong RAM nhưng bản bền còn — tải nó (APM-H-m).
        if (event.event === 'gap') void loadPersistedRun(runId);
      },
    });

    try {
      const result = await call(runId);

      if (options.describe) {
        aiRunStore.setResult(runId, options.describe(result as never));
      }
      // ⚠️ Trước `settle`: đặt câu hỏi lên bảng cũng chốt trạng thái sang "đang
      // chờ bạn trả lời", nên `settle` phải thấy nó rồi mới quyết.
      if (options.clarify) {
        aiRunStore.setClarify(runId, options.clarify(result as never, runId));
      }
      // Chốt trạng thái nếu bước `done` chưa tới (hoặc không bao giờ tới).
      aiRunStore.settle(runId);
      if (options.apply) {
        aiRunStore.setApply(runId, applyOf(options, result, runId));
      }
      return result;
    } catch (err) {
      // Thông điệp lấy từ chính lỗi HTTP, không dịch lại ở đây: `errorMessage`
      // của màn hình gọi đã có mã lỗi có cấu trúc và bản dịch của nó.
      aiRunStore.fail(runId, err instanceof Error ? err.message : 'Lỗi không rõ');
      throw err;
    } finally {
      // Không abort ở đây: bước `done` có thể tới **sau** phản hồi HTTP một
      // nhịp (server đẩy stream rồi mới trả body), và cắt sớm thì bảng theo dõi
      // đứng mãi ở bước kế cuối. Store tự cắt khi thấy bước kết, và lượt chạy
      // sau cũng cắt lượt trước.
    }
  }, []);
}

/**
 * Tải dấu vết bền của run sau `gap`. Đường HTTP thường, đã qua guard: chủ run
 * đọc được run của mình (`AiRunRepository.findForReader`). Lỗi thì thôi — stream
 * sẽ thử lại, và bảng theo dõi vẫn hiện những bước đã có.
 */
async function loadPersistedRun(runId: string): Promise<void> {
  try {
    const record = await apiFetch<AiRunRecord>(`/ai/harness/runs/${runId}`);
    aiRunStore.hydrate(runId, record.steps, record.status);
  } catch {
    // Có chủ đích: xem chú thích trên.
  }
}

/** Bọc `apply` để bấm một lần rồi đánh dấu đã áp dụng. */
function applyOf<T>(options: TrackOptions, result: T, runId: string): AiRunView['apply'] {
  const apply = options.apply?.(result as never);
  if (!apply) return null;

  return {
    label: apply.label,
    run: async () => {
      await apply.run();
      aiRunStore.markApplied(runId);
    },
  };
}
